import {
	required as _required,
	email as _email,
	minLength as _minLength,
	maxLength as _maxLength,
	helpers,
} from '@vuelidate/validators';
import { createI18nMessage } from '@vuelidate/validators';
import { unref } from 'vue';
import i18n from '@/i18n';

// Create a wrapper with the global t function`
const withI18nMessage = createI18nMessage({
	t: i18n.global.t.bind(i18n.global),
	messagePath: ({ $validator }) => `forms.errors.${$validator}`,
});

// Custom password validator
const passwordRegex =
	/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^\&*'\)\(+=._-])(?=.{10,})/;
const _password = helpers.regex(passwordRegex);

// Wrap validators
export const required = withI18nMessage(_required);
export const email = withI18nMessage(_email);
export const minLength = withI18nMessage(_minLength, { withArguments: true });
export const maxLength = withI18nMessage(_maxLength, { withArguments: true });

export const sameAs = (equalTo: unknown, params: any = {}) => {
	const getComparisonValue = () => {
		if (typeof equalTo === 'function') {
			return (equalTo as () => unknown)();
		}
		return unref(equalTo);
	};

	return helpers.withMessage(
		({ $params }) => i18n.global.t('forms.errors.sameAs', $params),
		helpers.withParams({ type: 'sameAs', ...params }, (value: unknown) => {
			return value === getComparisonValue();
		})
	);
};
export const password = helpers.withMessage(
	() => i18n.global.t('forms.errors.password'),
	helpers.withParams({ type: 'password' }, _password)
);

/**
 * vue-tsc cannot infer vuelidate's validation state under `<script setup>`, so
 * components describe the field shape themselves and cast at that boundary.
 */
export type VuelidateField = {
	$touch: () => void;
	$validate: () => Promise<boolean>;
	$error: boolean;
	$errors: Array<{ $message: string }>;
};

export type VuelidateFormState = Record<string, VuelidateField> | undefined;

// Also reads a field that was never cast, whose `$message` is `string | Ref<string>`.
export const fieldErrorMessage = (field?: {
	$error?: boolean;
	$errors?: readonly { $message?: unknown }[];
}): string => (field?.$error ? String(field.$errors?.[0]?.$message ?? '') : '');

export const useValidationRules = () => {
	return {
		required,
		email,
		minLength,
		maxLength,
		sameAs,
		password,
	};
};
